import curry from 'lodash/curry'
import ReactDOM from 'react-dom'
import React, {useEffect, useState} from 'react'
import {handle, raise, useEvent} from './events'
import {Image} from 'react-konva'
import useImage from 'use-image'
import * as Behaviours from 'alcumus-behaviours'
import {proportional} from './proportional'
import {getReturnValue} from './system/sprite'

export function getRelativePointerPosition(node) {
    // the function will return pointer position relative to the passed node
    var transform = node.getAbsoluteTransform().copy()
    // to detect relative position we need to invert transform
    transform.invert()

    // get pointer (say mouse or touch) position
    var pos = node.getStage().getPointerPosition()

    // now we find relative point
    return transform.point(pos)
}

let id = 0

export function useRefresh() {
    const [, refresh] = useState(id++)
    return () => {
        refresh(id++)
    }
}

export function mouseHandler(name, offset = {x: 0, y: 0}, scale) {
    function mouseMove(e) {
        const position = getRelativePointerPosition(e.target)
        position.x += offset.x
        position.y += offset.y
        if (scale) {
            position.x = proportional(position.x, true)
            position.y = proportional(position.y, true)
        }
        raise(`${name}.mouseMove`, [position.x, position.y])
    }

    function mouseDown(e) {
        const position = getRelativePointerPosition(e.target)
        position.x += offset.x
        position.y += offset.y
        if (scale) {
            position.x = proportional(position.x, true)
            position.y = proportional(position.y, true)
        }
        raise(`${name}.mouseDown`, [position.x, position.y])
    }

    function mouseUp(e) {
        const position = getRelativePointerPosition(e.target)
        position.x += offset.x
        position.y += offset.y
        if (scale) {
            position.x = proportional(position.x, true)
            position.y = proportional(position.y, true)
        }
        raise(`${name}.mouseUp`, [position.x, position.y])
    }

    return {
        onMouseMove: mouseMove,
        onMouseDown: mouseDown,
        onMouseUp: mouseUp,
        onTouchStart: mouseDown,
        onTouchMove: mouseMove,
        onTouchEnd: mouseUp,
    }
}

export function lerp(from, to, i) {
    i = Math.max(0, Math.min(1, i))
    return ((to - from) * i) + from

}

const DUMMY_OFFSET = {x: 0, y: 0}

export function genericSprite(sprite, offset = DUMMY_OFFSET) {
    return function Sprite() {
        let [image] = useImage(sprite)
        if (image) {
            this.width = image.width
            this.height = image.height
        }
        return !image ? null : (
            <Image
                x={-image.width / 2 + offset.x}
                y={-image.height / 2 + offset.y}
                // scaleX={0.6} scaleY={0.6}
                image={image}
            />
        )
    }
}

export function useAnimationFrame(fn) {
    const refresh = useRefresh()
    useEffect(() => {
        let cancelled = false

        function cb() {
            ReactDOM.unstable_batchedUpdates(fn)
            refresh()
            if (!cancelled) requestAnimationFrame(cb)
        }

        requestAnimationFrame(cb)
        return () => {
            cancelled = true
        }
    }, [])
}

export function useGameElement(Element, props, ...context) {
    useEvent(
        'gameSurfaceElements',
        (array) => {
            context.every((a) => !!a) && array.push({Element, props})
        },
        context
    )
}

export function offset(position, x, y) {
    let val = getReturnValue()
    val.x = position.x + x
    val.y = position.y + y
    return val
}

export function gameElement(Element) {
    handle('gameSurfaceElements', (array) => {
        array.push({Element, props: null})
    })
}

export const namespace = curry(function namespace(state, name, defaults) {
    return (state[name] = state[name] || {...defaults})
}, 2)

export function intersect(x1, y1, x2, y2, x3, y3, x4, y4) {
    // Check if none of the lines are of length 0
    if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
        return false
    }

    let denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1)

    // Lines are parallel
    if (denominator === 0) {
        return false
    }

    let ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator
    let ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator

    // is the intersection along the segments
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
        return false
    }

    // Return a object with the x and y coordinates of the intersection
    let x = x1 + ua * (x2 - x1)
    let y = y1 + ua * (y2 - y1)

    return [x, y]
}

export function register(...params) {
    Behaviours.register(...params)
    return {
        config(obj) {
            return {[params[0]]: {...obj}}
        },
    }
}

export function getDirection(target, src, scale = 1) {
    let dx = target.x - src.x
    let dy = target.y - src.y
    let l = Math.sqrt(dx * dx + dy * dy)
    return {x: (dx / l) * scale, y: (dy / l) * scale, l}
}
